import * as THREE from 'three';

// Define a more flexible interface for controls
export interface CameraControls {
  target?: THREE.Vector3;
  update?: () => void;
}

/**
 * Centers the camera on a loaded 3D object
 * @param loadedObject - The 3D object to center the camera on
 * @param camera - The camera to position
 * @param controls - The OrbitControls instance (optional, can be null)
 */
export function centerCameraOnModel(
  loadedObject: THREE.Object3D,
  camera: THREE.Camera,
  controls?: CameraControls | null,
): void {
  if (!loadedObject) return;
  const box = new THREE.Box3().setFromObject(loadedObject);
  centerCameraOnBox(box, camera, controls);
}

/**
 * Same as centerCameraOnModel, but accepts a pre-computed world-space Box3.
 * Useful when callers accumulate bounds across multiple scene objects that
 * share transforms (e.g. uploaded models wrapped in a `<group scale=...>`),
 * where cloning the meshes to pass to centerCameraOnModel would sever those
 * parent transforms.
 */
export function centerCameraOnBox(box: THREE.Box3, camera: THREE.Camera, controls?: CameraControls | null): void {
  if (box.isEmpty()) return;

  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());

  // Calculate the distance needed to fit the model in the view
  const maxDim = Math.max(size.x, size.y, size.z);
  const fov = camera instanceof THREE.PerspectiveCamera ? camera.fov * (Math.PI / 180) : 45 * (Math.PI / 180);
  const distance = maxDim / (2 * Math.tan(fov / 2));

  // Position the camera to look at the center of the model
  // We're using the existing camera position direction but adjusting the distance
  const direction = new THREE.Vector3(0, 0, 1).normalize();
  const position = center.clone().add(direction.multiplyScalar(distance * 0.85));

  // Set camera position and look at the center of the model
  camera.position.copy(position);
  camera.lookAt(center);

  if (camera instanceof THREE.PerspectiveCamera) {
    camera.updateProjectionMatrix();
  }

  // If using OrbitControls, update the target (check for null and required methods first)
  if (controls && controls.target && controls.update) {
    controls.target.copy(center);
    controls.update();
  }
}
